import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Global from '../../store/global-selectors';
import { EditColumnsModal } from '../../components/modals/edit-columns-modal';
import baseColumns from '../../pages/products-page/inventory/columns';
import { useEnableStockAudit } from '../../feature-flags/hooks/use-enable-stock-audit';
import {
  baseColumnsDefaultConfigs,
  ActiveShippingLocationsColumnId,
  AuditColumnId,
} from '../../store/inventory/grid/default-column-configuration';
import { saveUserSettingGridConfig } from '../../store/user/user.actions';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { useUserSetting } from '../../hooks/use-user-setting';
import { useShouldDisplayAutoRoutingControls } from '../../hooks/auto-routing/use-should-display-auto-routing-controls';
import { useIsAutoSplitEnabledForSeller } from '../../hooks/use-is-auto-splitting-enabled-for-seller';
import { InventoryGridViewIds } from '../../constants/grid-view-ids';

const tempDefaultColumnConfigurations = baseColumnsDefaultConfigs.map((col) => {
  const mappedBase = baseColumns[col.id] || baseColumns[col.mappedId];
  return {
    ...col,
    getMessageDescriptor: mappedBase?.getMessageDescriptor,
  };
});

export const EditInventoryColumnsModalContainer = ({ onClose }) => {
  const dispatch = useDispatch();
  const saveColumnConfiguration = useCallback(
    (columns) => {
      dispatch(
        saveUserSettingGridConfig(InventoryGridViewIds.INVENTORY, InventoryGridViewIds.INVENTORY, InventoryGridViewIds.DEFAULT, columns),
      );
    },
    [dispatch],
  );

  const shouldUseAggregateInventory = useSelector(Global.selectProductshouldUseAggregateInventory);
  const userSetColumnConfiguration = useUserSetting(UserSettings.inventoryGridDefaultConfig.settingName);
  const tempUserColumnConfig = JSON.parse(userSetColumnConfiguration || '[]');

  const shouldDisplayAutoRoutingControls = useShouldDisplayAutoRoutingControls();
  const useAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const isStockAuditEnabled = useEnableStockAudit();

  const applyColumnConfigurationFilters = useCallback(
    (column) => {
      const filteredColumnMap = {
        AutoSplit: useAutoSplitEnabled,
        AggregrateInventoryCounts: shouldUseAggregateInventory,
      };

      if (column.id === ActiveShippingLocationsColumnId && !shouldDisplayAutoRoutingControls) {
        return false;
      }

      if (column.id === AuditColumnId && !isStockAuditEnabled) {
        return false;
      }

      if (filteredColumnMap[column.id] !== undefined) {
        return filteredColumnMap[column.id];
      }

      return true;
    },
    [shouldDisplayAutoRoutingControls, shouldUseAggregateInventory, useAutoSplitEnabled, isStockAuditEnabled],
  );

  const userColumnConfig = tempUserColumnConfig.filter(applyColumnConfigurationFilters);
  const defaultColumnConfigurations = tempDefaultColumnConfigurations.filter(applyColumnConfigurationFilters);

  const columnConfiguration = userColumnConfig.length
    ? userColumnConfig.map((userConf) => {
        const col = defaultColumnConfigurations.find((colConf) => colConf.id === userConf.id);
        return {
          ...col,
          ...userConf,
        };
      })
    : defaultColumnConfigurations;

  return (
    <EditColumnsModal
      baseColumns={defaultColumnConfigurations}
      columnConfiguration={columnConfiguration}
      defaultColumnConfigurations={defaultColumnConfigurations}
      onClose={onClose}
      saveColumnConfiguration={saveColumnConfiguration}
    />
  );
};

EditInventoryColumnsModalContainer.propTypes = {
  onClose: PropTypes.func.isRequired,
};
